import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Location, getDistanceBetween } from '../models/Location';

/**
 * This Angular service should be injected into any component that wants to
 * know the user's current location. The `location` instance variable is an
 * RxJS BehaviorSubject that can be subscribed to so that a client
 * can react to changes when the value inside location changes.
 *
 * Usage:
 * <tab> locationService.location.value
 * <tab> locationService.location.asObservable()
 */
@Injectable()
class LocationService {
  public location: BehaviorSubject<Location>;
  private geoErrorAlerted = false;

  constructor() {
    this.location = new BehaviorSubject<Location>(null);
    this.updateCurrentLocation();
  }

  public async updateCurrentLocation(): Promise<Location> {
    const loc = await this.getCurrentLocation();
    this.location.next(loc);
    return loc;
  }

  public getDistanceBetweenCurrentAndDestination(destination: Location): number {
    return getDistanceBetween({
      origin: this.location.value,
      destination
    });
  }

  private async getCurrentLocation(): Promise<Location> {
    return new Promise<Location>((resolve, reject) => {
      const defaultLocation: Location = {
        lat: 45.5426866,
        lng: -122.7245971
      };
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(position => {
          resolve(<Location>{
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        }, (err) => {
          resolve(defaultLocation);
        });
      } else {
          if (!this.geoErrorAlerted) {
            window.alert('Please enable geolocation in your browser');
            this.geoErrorAlerted = true;
          }
        resolve(defaultLocation);
      }
  });
  }
}

export { LocationService, Location };
